import { tmPricingGap } from '@/lib/sales/pricingMode';

/**
 * The single decision point for "may this estimate be written?". Every create /
 * update entry point on the estimate form (Save and Save & Send) reads this
 * result, so no write path can drift into its own rule set. The create-mode
 * payment-schedule CTA only edits local form state and is intentionally outside
 * this write gate.
 *
 * The rules mirror what the operations service actually enforces on
 * POST /estimates and PATCH /estimates/:id — nothing stricter (a contact is
 * optional server-side, a customer name is not) and nothing looser (the deposit
 * rule used to 400 with no client check at all). The one
 * client-only rules are LINE_ITEMS_REQUIRED and TM_SERVICE_UNPRICED: the server
 * allows an empty draft and a $0 line, and neither is ever what the user meant.
 */
export type EstimateSubmitGateCode =
  | 'BUSINESS_UNIT_REQUIRED'
  | 'CUSTOMER_REQUIRED'
  | 'LINE_ITEMS_REQUIRED'
  | 'ADDRESS_INVALID'
  | 'CUSTOM_LINE_UNNAMED'
  | 'TM_HOURS_REQUIRED'
  | 'TM_SERVICE_UNPRICED'
  | 'MEMBERSHIP_PRICING_LOADING'
  | 'MEMBERSHIP_PRICING_UNAVAILABLE'
  | 'DISCOUNT_EXCEEDS_PERCENT'
  | 'DISCOUNT_EXCEEDS_SUBTOTAL'
  | 'DEPOSIT_AMOUNT_REQUIRED'
  | 'DEPOSIT_EXCEEDS_PERCENT'
  | 'DEPOSIT_EXCEEDS_TOTAL'
  | 'PAYMENT_SCHEDULE_INVALID';

/** Inline field errors a failure asks the form to reveal before it toasts. */
export type EstimateSubmitGateReveal = 'addressErrors';

export interface EstimateSubmitGateLine {
  name: string;
  code?: string | null;
  type?: string | null;
  priceStrategy?: string | null;
  estimatedHours?: number | null;
  /**
   * The two a MATERIALIZED service answers with instead of `estimatedHours`. It
   * keeps its time in `dynamicPricingConfig.laborLines` and carries its whole
   * already-calculated price in `basePrice`, so without them the T&M rule below
   * reads a complete materials-only service as an empty one.
   */
  basePrice?: number | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
}

export interface EstimateSubmitGateInput {
  businessUnitBlockedReason?: string | null;
  /**
   * Deliberately not read by any rule. It stays on the input because the
   * ad-hoc-customer case — a stored estimate with no contact record — is only
   * expressible with it, and because its absence is the point: the old client
   * predicate required contactId while the server marks it optional and
   * requires customer.name instead, which is why such estimates could never be
   * re-saved. See the CUSTOMER_REQUIRED rule below.
   */
  contactId: string | null;
  customerName: string;
  lineItems: EstimateSubmitGateLine[];
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  membershipPricingState: 'ready' | 'loading' | 'error';
  /** Pre-computed first address error, already labeled ("Service address: …"). */
  addressValidationError: string | null;
  discountType: '$' | '%';
  discountValue: number;
  /** Selected-line subtotal — the same basis the pricing engine discounts against. */
  subtotal: number;
  depositRequired: boolean;
  depositType: '$' | '%';
  depositValue: number;
  /**
   * The base the deposit is charged against (grand total minus any membership
   * line) — the same one the server caps `depositAmount` at.
   */
  depositEligibleTotal: number;
  /**
   * Is the deposit being SET by this save — different from what the saved
   * estimate holds (or a new estimate)? The bound is a rule about input: the
   * server judges only a deposit the request sets, and keeps an estimate
   * storing an oversized one, or whose total shrank under a flat one,
   * editable. Gating Save on the live figure alone stranded those edits.
   */
  depositChanged: boolean;
  /** First current validation error for a locally authored schedule, if any. */
  paymentScheduleValidationError?: string | null;
}

export interface EstimateSubmitGateFailure {
  code: EstimateSubmitGateCode;
  message: string;
  /** Inline field errors to reveal before toasting this failure. */
  reveal?: EstimateSubmitGateReveal;
}

export type EstimateSubmitGateResult =
  | { ok: true }
  | (EstimateSubmitGateFailure & {
      ok: false;
      /**
       * The first failure that greys the submit buttons out, which is not
       * always the first failure overall: the two reveal-bearing rules stay
       * click-time (a disabled button can't be clicked, so their inline field
       * errors would never be revealed). Every rule is evaluated, so one of
       * those ordered ahead of a hard failure can no longer hide it — the click
       * reports the failure above, the buttons report this one.
       */
      blocking: EstimateSubmitGateFailure | null;
    });

/** A failure paired with whether it greys the submit buttons out. */
interface EvaluatedRule {
  blocksSave: boolean;
  failure: EstimateSubmitGateFailure;
}

export function evaluateEstimateSubmitGate(
  input: EstimateSubmitGateInput,
): EstimateSubmitGateResult {
  const rules: EvaluatedRule[] = [];
  const blockSave = (failure: EstimateSubmitGateFailure) =>
    rules.push({ blocksSave: true, failure });
  const revealOnClick = (failure: EstimateSubmitGateFailure) =>
    rules.push({ blocksSave: false, failure });

  if (input.businessUnitBlockedReason) {
    blockSave({ code: 'BUSINESS_UNIT_REQUIRED', message: input.businessUnitBlockedReason });
  }

  // `customer.name` is what the server actually requires; `contactId` is
  // optional there. Selecting a contact fills the name, so this still reads as
  // "pick someone" — but it no longer bricks an estimate stored without a
  // contact record, and it no longer lets a nameless payload reach a 400.
  if (!input.customerName.trim()) {
    blockSave({ code: 'CUSTOMER_REQUIRED', message: 'Please select a contact' });
  }

  if (input.lineItems.length === 0) {
    blockSave({ code: 'LINE_ITEMS_REQUIRED', message: 'Add at least one line item' });
  }

  if (input.addressValidationError) {
    revealOnClick({
      code: 'ADDRESS_INVALID',
      message: input.addressValidationError,
      reveal: 'addressErrors',
    });
  }

  const unnamedCustomLine = input.lineItems.find((line) => !line.code && !line.name.trim());
  if (unnamedCustomLine) {
    blockSave({ code: 'CUSTOM_LINE_UNNAMED', message: 'Name each custom line item' });
  }

  // A T&M service has to be able to produce a price. WHICH fact is missing
  // depends on whether the T&M switch materialized it: an unmaterialized service
  // is still waiting on `hours x rate`, while a materialized one keeps its time
  // in `laborLines` and carries its calculated price in `basePrice` — so a
  // materials-only service, real parts and zero hours, is COMPLETE and the
  // `estimatedHours > 0` spelling this used to carry refused it. `tmPricingGap`
  // is the one answer, shared with the estimate template's own validation.
  const invalidTMLine = input.lineItems
    .map((line) => ({
      line,
      gap: tmPricingGap({ ...line, isCustom: !line.code }, input.pricingMode),
    }))
    .find((entry) => entry.gap !== null);
  if (invalidTMLine?.gap === 'HOURS') {
    blockSave({
      code: 'TM_HOURS_REQUIRED',
      message: `Set hours for T&M service: ${invalidTMLine.line.name}`,
    });
  } else if (invalidTMLine?.gap === 'PRICE') {
    blockSave({
      code: 'TM_SERVICE_UNPRICED',
      message: `${invalidTMLine.line.name} prices at $0 — give it labor hours or parts.`,
    });
  }

  if (input.membershipPricingState === 'loading') {
    blockSave({
      code: 'MEMBERSHIP_PRICING_LOADING',
      message: 'Wait for membership pricing to finish loading before saving.',
    });
  } else if (input.membershipPricingState === 'error') {
    blockSave({
      code: 'MEMBERSHIP_PRICING_UNAVAILABLE',
      message: 'Membership pricing is unavailable. Retry before saving.',
    });
  }

  // NOT-242: strict `>`, so an exact-subtotal discount → $0 total is allowed.
  if (input.discountType === '%' && input.discountValue > 100) {
    blockSave({ code: 'DISCOUNT_EXCEEDS_PERCENT', message: 'Discount can’t exceed 100%.' });
  }
  if (input.discountType === '$' && input.discountValue > input.subtotal) {
    blockSave({
      code: 'DISCOUNT_EXCEEDS_SUBTOTAL',
      message: 'Discount can’t exceed the subtotal.',
    });
  }

  // NOT-244: the server rejects a required deposit with no amount. Reachable by
  // reopening a stored estimate saved that way, which used to 400 on every save.
  if (input.depositRequired && input.depositValue <= 0) {
    blockSave({
      code: 'DEPOSIT_AMOUNT_REQUIRED',
      message: 'Enter a deposit amount, or turn off “Require deposit before scheduling”.',
    });
  }
  // The server caps `depositAmount` at the total but stores `depositValue` as
  // typed, so an oversized deposit made the builder and the PDF disagree for
  // good. Mirrors the discount rule; strict `>`, so a 100% deposit is allowed.
  // Not gated on `depositRequired`: an optional deposit is still quoted and
  // collected after approval, and it was saved as typed and charged capped
  // (NOT-716).
  const judgeDepositBound = input.depositChanged;
  if (judgeDepositBound && input.depositType === '%' && input.depositValue > 100) {
    blockSave({ code: 'DEPOSIT_EXCEEDS_PERCENT', message: 'Deposit can’t exceed 100%.' });
  }
  if (
    judgeDepositBound &&
    input.depositType === '$' &&
    input.depositValue > input.depositEligibleTotal
  ) {
    blockSave({ code: 'DEPOSIT_EXCEEDS_TOTAL', message: 'Deposit can’t exceed the total.' });
  }

  if (input.paymentScheduleValidationError) {
    blockSave({
      code: 'PAYMENT_SCHEDULE_INVALID',
      message: `Update the payment schedule: ${input.paymentScheduleValidationError}`,
    });
  }

  const first = rules[0];
  if (!first) return { ok: true };
  return {
    ok: false,
    ...first.failure,
    blocking: rules.find((rule) => rule.blocksSave)?.failure ?? null,
  };
}

/** True when the submit buttons should stay clickable. */
export function canSubmitEstimate(result: EstimateSubmitGateResult): boolean {
  return result.ok || result.blocking === null;
}

/**
 * The reason to render beside a greyed-out submit button. Null while the buttons
 * are live — the reveal-bearing failures explain themselves on click instead.
 */
export function estimateSubmitBlockedReason(result: EstimateSubmitGateResult): string | null {
  return result.ok ? null : (result.blocking?.message ?? null);
}
